// Groups map listings per area (regso) and listing type. Zoomed out, each area shows one group per
// type; zooming in splits each into up to MAX_GROUPS_PER_AREA groups, and close in every listing is
// shown on its own. Used by both kartvy (Map.js) and listvy (ListViewPage.js).

export const MAX_GROUPS_PER_AREA = 4;
// Zoom levels below maxZoom where listings stop being grouped (matches the earlier clustering).
const SINGLES_ZOOM_OFFSET = 3;
const KMEANS_ITERATIONS = 10;

// Markers closer than this on screen (px) are drawn as one group, so none hide behind another.
export const OVERLAP_DISTANCE_PX = 25;
const MAX_MERGE_PASSES = 6;

// Groups per area and type at a zoom level; Infinity means every listing on its own.
export const getGroupsPerArea = (zoom, maxZoom) => {
    if (zoom >= maxZoom - SINGLES_ZOOM_OFFSET) return Infinity;
    if (zoom < 9) return 1;
    if (zoom < 10) return 2;
    if (zoom < 11) return 3;
    return MAX_GROUPS_PER_AREA;
};

const makeGroup = (listings) => ({
    listings,
    count: listings.length,
    lat: listings.reduce((sum, listing) => sum + listing.lat, 0) / listings.length,
    lng: listings.reduce((sum, listing) => sum + listing.lng, 0) / listings.length,
});

// Deterministic k-means: farthest-first starting points, so groups don't shift between renders.
const splitIntoGroups = (listings, k) => {
    if (k <= 1) return [makeGroup(listings)];
    if (listings.length <= k) return listings.map((listing) => makeGroup([listing]));

    // Scale longitude so distances are roughly even in both directions at Swedish latitudes.
    const meanLat = listings.reduce((sum, listing) => sum + listing.lat, 0) / listings.length;
    const lngScale = Math.cos((meanLat * Math.PI) / 180);
    const points = listings.map((listing) => [listing.lng * lngScale, listing.lat]);
    const distSq = (a, b) => ((a[0] - b[0]) ** 2) + ((a[1] - b[1]) ** 2);

    const centroid = [
        points.reduce((sum, point) => sum + point[0], 0) / points.length,
        points.reduce((sum, point) => sum + point[1], 0) / points.length,
    ];
    const centers = [];
    let first = 0;
    points.forEach((point, index) => {
        if (distSq(point, centroid) > distSq(points[first], centroid)) first = index;
    });
    centers.push(points[first]);
    while (centers.length < k) {
        let bestIndex = -1;
        let bestDist = 0;
        points.forEach((point, index) => {
            const nearest = Math.min(...centers.map((center) => distSq(point, center)));
            if (nearest > bestDist) {
                bestDist = nearest;
                bestIndex = index;
            }
        });
        // Fewer distinct locations than k (e.g. one building): stop with what we have.
        if (bestIndex < 0) break;
        centers.push(points[bestIndex]);
    }

    const assignment = new Array(points.length).fill(-1);
    for (let iteration = 0; iteration < KMEANS_ITERATIONS; iteration += 1) {
        let changed = false;
        points.forEach((point, index) => {
            let nearest = 0;
            for (let c = 1; c < centers.length; c += 1) {
                if (distSq(point, centers[c]) < distSq(point, centers[nearest])) nearest = c;
            }
            if (assignment[index] !== nearest) {
                assignment[index] = nearest;
                changed = true;
            }
        });
        if (!changed) break;
        centers.forEach((_, c) => {
            const members = points.filter((__, index) => assignment[index] === c);
            if (members.length) {
                centers[c] = [
                    members.reduce((sum, point) => sum + point[0], 0) / members.length,
                    members.reduce((sum, point) => sum + point[1], 0) / members.length,
                ];
            }
        });
    }

    return centers
        .map((_, c) => listings.filter((__, index) => assignment[index] === c))
        .filter((members) => members.length)
        .map(makeGroup);
};

// Nyproduktion's marker colour in both views (its groups are separate from the type groups).
export const NEW_CONSTRUCTION_COLOR = '#9c27b0';

// Builds the per-area, per-type buckets once per listing set. Listings without an area code
// get a bucket of their own, so they are always shown individually.
export const buildAreaGroupIndex = (listings, getAreaCode, getTypeKey) => {
    const buckets = new Map();
    (listings || []).forEach((listing, index) => {
        const lat = Number(listing?.lat);
        const lng = Number(listing?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const typeKey = getTypeKey(listing.type, listing);
        const areaCode = String(getAreaCode(listing) || '').trim() || `listing:${listing?.id ?? index}`;
        const bucketKey = `${areaCode}|${typeKey}`;
        let bucket = buckets.get(bucketKey);
        if (!bucket) {
            bucket = {
                key: bucketKey,
                areaCode,
                typeKey,
                listings: [],
                splits: new Map(),
                south: lat,
                north: lat,
                west: lng,
                east: lng,
            };
            buckets.set(bucketKey, bucket);
        }
        bucket.listings.push(listing);
        bucket.south = Math.min(bucket.south, lat);
        bucket.north = Math.max(bucket.north, lat);
        bucket.west = Math.min(bucket.west, lng);
        bucket.east = Math.max(bucket.east, lng);
    });
    return { buckets: [...buckets.values()] };
};

// Groups whose position is inside bounds ({ south, west, north, east }), as
// { key, typeKey, count, lat, lng, listings }. Single listings are keyed by listing id.
export const getAreaGroups = (index, groupsPerArea, bounds) => {
    const groups = [];
    const inBounds = (lat, lng) => lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
    index.buckets.forEach((bucket) => {
        if (bucket.north < bounds.south || bucket.south > bounds.north || bucket.east < bounds.west || bucket.west > bounds.east) {
            return;
        }
        if (!Number.isFinite(groupsPerArea)) {
            bucket.listings.forEach((listing) => {
                if (!inBounds(listing.lat, listing.lng)) return;
                groups.push({
                    key: `p:${bucket.typeKey}:${listing.id}`,
                    areaCode: bucket.areaCode,
                    typeKey: bucket.typeKey,
                    count: 1,
                    lat: listing.lat,
                    lng: listing.lng,
                    listings: [listing],
                });
            });
            return;
        }
        const k = Math.min(groupsPerArea, bucket.listings.length);
        if (!bucket.splits.has(k)) bucket.splits.set(k, splitIntoGroups(bucket.listings, k));
        bucket.splits.get(k).forEach((group, groupIndex) => {
            if (!inBounds(group.lat, group.lng)) return;
            groups.push({
                key: group.count === 1
                    ? `p:${bucket.typeKey}:${group.listings[0].id}`
                    : `g:${bucket.key}:${k}:${groupIndex}`,
                areaCode: bucket.areaCode,
                typeKey: bucket.typeKey,
                count: group.count,
                lat: group.lat,
                lng: group.lng,
                listings: group.listings,
            });
        });
    });
    return groups;
};

// One merge pass: each group (biggest first, then by key, so the input order doesn't matter) takes in
// the not yet taken groups of its area and type whose centres are closer than minDistancePx on screen.
// Groups never take in another area's homes: a group belongs to one area, so turning an area on or off
// never changes its neighbours' markers (two markers on either side of a border may overlap instead).
const mergePass = (groups, project, minDistancePx) => {
    const cell = minDistancePx;
    const points = groups.map((group) => project(group.lat, group.lng));
    const cellKey = (group, x, y) => `${group.areaCode}|${group.typeKey}|${x}|${y}`;
    const grid = new Map();
    groups.forEach((group, index) => {
        const key = cellKey(group, Math.floor(points[index].x / cell), Math.floor(points[index].y / cell));
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key).push(index);
    });
    const order = groups.map((_, index) => index).sort((a, b) => (
        (groups[b].count - groups[a].count) || (groups[a].key < groups[b].key ? -1 : 1)
    ));
    const taken = new Array(groups.length).fill(false);
    const next = [];
    let merged = false;
    order.forEach((index) => {
        if (taken[index]) return;
        taken[index] = true;
        const group = groups[index];
        const point = points[index];
        const cx = Math.floor(point.x / cell);
        const cy = Math.floor(point.y / cell);
        const members = [group];
        for (let dx = -1; dx <= 1; dx += 1) {
            for (let dy = -1; dy <= 1; dy += 1) {
                (grid.get(cellKey(group, cx + dx, cy + dy)) || []).forEach((other) => {
                    if (taken[other]) return;
                    const otherPoint = points[other];
                    if (((otherPoint.x - point.x) ** 2) + ((otherPoint.y - point.y) ** 2) >= minDistancePx ** 2) return;
                    taken[other] = true;
                    members.push(groups[other]);
                });
            }
        }
        if (members.length === 1) {
            next.push(group);
            return;
        }
        merged = true;
        const listings = members.flatMap((member) => member.listings);
        next.push({
            ...makeGroup(listings),
            key: `m:${members.map((member) => member.key).sort()[0]}:${listings.length}`,
            areaCode: group.areaCode,
            typeKey: group.typeKey,
        });
    });
    return { groups: next, merged };
};

// Merges groups of the same area and type whose markers would overlap on screen (centres closer than
// minDistancePx at this zoom) into one group with a count. project(lat, lng) gives screen pixels.
// Merging moves a group's centre, so it repeats until nothing overlaps.
export const mergeOverlappingGroups = (groups, project, minDistancePx = OVERLAP_DISTANCE_PX) => {
    let current = groups;
    for (let pass = 0; pass < MAX_MERGE_PASSES; pass += 1) {
        const result = mergePass(current, project, minDistancePx);
        current = result.groups;
        if (!result.merged) break;
    }
    return current;
};

// Screen offsets (px) for `count` markers fanned out around a point: rings far enough apart that no two
// overlap, the first ring holding as many as fit.
export const getSpreadOffsets = (count, minDistancePx = OVERLAP_DISTANCE_PX) => {
    // At least 46px apart: a shape marker is ~34px across with its halo, so a tighter ring let adjacent
    // halos blend into a scuffed edge.
    const spacing = Math.max(minDistancePx + 4, 46);
    const offsets = [];
    let ring = 1;
    while (offsets.length < count) {
        const radius = spacing * ring;
        const fits = Math.max(1, Math.floor((2 * Math.PI * radius) / spacing));
        const inRing = Math.min(fits, count - offsets.length);
        for (let i = 0; i < inRing; i += 1) {
            // Starts at the top and goes clockwise.
            const angle = (-Math.PI / 2) + ((2 * Math.PI * i) / inRing);
            offsets.push({ x: radius * Math.cos(angle), y: radius * Math.sin(angle) });
        }
        ring += 1;
    }
    return offsets;
};

// The groups drawn at a zoom: the area groups, with overlapping ones merged.
// options.project(lat, lng, zoom) gives screen pixels at that zoom (unproject(x, y, zoom) the reverse).
// options.spreads ([{ ids, lat, lng, zoom }], or one as options.spread): homes still on top of each other at the
// closest zoom that the user clicked apart; at that zoom each is drawn on its own around its point, so all can
// be clicked. Several can be open at once (the owner, 2026-11-01): each ring keeps clear of the other markers,
// of the homes earlier rings placed and of the other spreads' centres, so opening one never moves another.
export const getMapGroups = (index, zoom, maxZoom, bounds, {
    project,
    unproject,
    minDistancePx = OVERLAP_DISTANCE_PX,
    spread = null,
    spreads = null,
} = {}) => {
    let groups = getAreaGroups(index, getGroupsPerArea(zoom, maxZoom), bounds);
    const active = unproject
        ? (spreads || (spread ? [spread] : [])).filter((item) => item && item.zoom === zoom)
        : [];
    // Each spread's homes out of their groups (a home in two spreads stays with the first).
    const owner = new Map();
    active.forEach((item, i) => item.ids.forEach((id) => {
        if (!owner.has(id)) owner.set(id, i);
    }));
    const pulled = active.map(() => []);
    if (owner.size) {
        const rest = [];
        groups.forEach((group) => {
            const inSpread = group.listings.filter((listing) => owner.has(listing.id));
            if (!inSpread.length) {
                rest.push(group);
                return;
            }
            inSpread.forEach((listing) => pulled[owner.get(listing.id)].push({ listing, typeKey: group.typeKey }));
            const others = group.listings.filter((listing) => !owner.has(listing.id));
            if (others.length) rest.push({ ...group, ...makeGroup(others), key: `${group.key}:rest` });
        });
        groups = rest;
    }
    const merged = mergeOverlappingGroups(groups, (lat, lng) => project(lat, lng, zoom), minDistancePx);
    if (!pulled.some((list) => list.length)) return merged;
    const taken = merged.map((group) => project(group.lat, group.lng, zoom));
    const centres = active.map((item) => project(item.lat, item.lng, zoom));
    const out = merged.slice();
    active.forEach((item, i) => {
        // Same order every time, so a listing keeps its place in the ring.
        const list = pulled[i].sort((a, b) => (String(a.listing.id) < String(b.listing.id) ? -1 : 1));
        if (!list.length) return;
        // Ring spots that would land on another marker are skipped (rings go further out instead).
        const centre = centres[i];
        const blockers = taken.concat(centres.filter((_, j) => j !== i));
        const isFree = (offset) => blockers.every((point) => (
            ((point.x - centre.x - offset.x) ** 2) + ((point.y - centre.y - offset.y) ** 2) >= minDistancePx ** 2
        ));
        const candidates = getSpreadOffsets(list.length + 60, minDistancePx);
        const free = candidates.filter(isFree);
        const offsets = free.length >= list.length ? free : candidates;
        list.forEach(({ listing, typeKey }, k) => {
            const x = centre.x + offsets[k].x;
            const y = centre.y + offsets[k].y;
            const position = unproject(x, y, zoom);
            taken.push({ x, y });
            out.push({
                key: `s:${listing.id}`,
                typeKey,
                count: 1,
                lat: position.lat,
                lng: position.lng,
                listings: [listing],
                spreadFrom: { lat: item.lat, lng: item.lng },
            });
        });
    });
    return out;
};

// The first zoom above `zoom` at which a group's listings no longer all end up in one marker, or
// null when they never split (several homes at one address): then the caller spreads them out.
export const findSplitZoom = (index, group, zoom, maxZoom, project, minDistancePx = OVERLAP_DISTANCE_PX) => {
    const listings = group?.listings || [];
    if (listings.length < 2) return null;
    const ids = new Set(listings.map((listing) => listing.id));
    const bounds = {
        south: Math.min(...listings.map((listing) => listing.lat)),
        north: Math.max(...listings.map((listing) => listing.lat)),
        west: Math.min(...listings.map((listing) => listing.lng)),
        east: Math.max(...listings.map((listing) => listing.lng)),
    };
    // Same spot for all of them: no zoom splits it.
    if (bounds.south === bounds.north && bounds.west === bounds.east) return null;
    // Room around the listings for neighbours that could merge into them.
    const padLat = Math.max(bounds.north - bounds.south, 0.002);
    const padLng = Math.max(bounds.east - bounds.west, 0.004);
    const queryBounds = {
        south: bounds.south - padLat,
        north: bounds.north + padLat,
        west: bounds.west - padLng,
        east: bounds.east + padLng,
    };
    for (let candidate = Math.floor(zoom) + 1; candidate <= maxZoom; candidate += 1) {
        const holding = getMapGroups(index, candidate, maxZoom, queryBounds, { project, minDistancePx })
            .filter((candidateGroup) => candidateGroup.listings.some((listing) => ids.has(listing.id)));
        if (holding.length > 1) return candidate;
    }
    return null;
};
